import axios from "axios";
import env from "../environment";
import { comNext, destinoDe } from "../utils/nextSeguro";

// Refresh token rotativo (PO1-1999). Quando uma chamada autenticada recebe 401,
// o interceptor chama POST /sessions/refresh aqui, atualiza o storage e
// retenta a request original. Single-flight via `inFlight` evita N refreshes
// concorrentes quando várias requests caem 401 ao mesmo tempo.

export const TOKEN_KEY = "@HubEverlog:token";
export const REFRESH_KEY = "@HubEverlog:refreshToken";
export const REFRESH_EXP_KEY = "@HubEverlog:refreshExpiresAt";
export const USER_KEY = "@HubEverlog:user";

// Cliente isolado: sem interceptor de retry, evita loop quando o próprio
// /sessions/refresh devolver 401.
const refreshClient = axios.create({
  baseURL: env.API_URL,
  headers: { "Content-Type": "application/json;charset=utf-8" },
});

let inFlight = null;
const subscribers = new Set();

export function onTokenRefreshed(handler) {
  subscribers.add(handler);
  return () => subscribers.delete(handler);
}

export function clearAuthStorage() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
  localStorage.removeItem(REFRESH_EXP_KEY);
  localStorage.removeItem(USER_KEY);
}

function redirectToLogin() {
  if (typeof window === "undefined") return;
  const currentPath = window.location.pathname || "";
  if (currentPath.startsWith("/auth")) return;
  // PO1-3391 (P3): sessão caiu no meio de um link do app → volta para ele
  // depois do login (só /app/, validado em nextSeguro). Aqui é reload inteiro,
  // então o destino tem que ir na URL, não no state do router.
  window.location.replace(comNext("/auth/login", destinoDe(window.location)));
}

export async function refreshAccessToken() {
  if (inFlight) return inFlight;
  const refreshToken = localStorage.getItem(REFRESH_KEY);
  if (!refreshToken) {
    return Promise.reject(new Error("no_refresh_token"));
  }
  inFlight = refreshClient
    .post("/sessions/refresh", { refreshToken })
    .then((res) => {
      const data = res.data || {};
      const newToken = data.token;
      const newRefresh = data.refreshToken;
      const newRefreshExp = data.refreshExpiresAt;
      if (!newToken || !newRefresh) {
        throw new Error("refresh_payload_invalido");
      }
      localStorage.setItem(TOKEN_KEY, newToken);
      localStorage.setItem(REFRESH_KEY, newRefresh);
      if (newRefreshExp) {
        const iso =
          newRefreshExp instanceof Date
            ? newRefreshExp.toISOString()
            : new Date(newRefreshExp).toISOString();
        localStorage.setItem(REFRESH_EXP_KEY, iso);
      }
      subscribers.forEach((fn) => {
        try {
          fn(newToken);
        } catch (_) {
          // subscriber não pode quebrar o fluxo de refresh
        }
      });
      return newToken;
    })
    .catch((err) => {
      // Refresh inválido / revogado / expirado → derruba a sessão local
      clearAuthStorage();
      throw err;
    })
    .finally(() => {
      inFlight = null;
    });
  return inFlight;
}

export function attachAuthInterceptor(axiosInstance) {
  axiosInstance.interceptors.response.use(
    (res) => res,
    async (error) => {
      const status = error && error.response && error.response.status;
      const config = error && error.config;
      // Não retentar chamadas de auth (login, refresh, logout) — refresh aqui
      // viraria recursão e login/logout não devem disparar renovação.
      const url = (config && config.url) || "";
      const isAuthEndpoint = url.indexOf("/sessions") !== -1 || url.indexOf("sessions") === 0;
      if (status !== 401 || !config || config._retry || isAuthEndpoint) {
        return Promise.reject(error);
      }
      config._retry = true;
      try {
        const newToken = await refreshAccessToken();
        config.headers = config.headers || {};
        // axios 0.19 não normaliza case do header. Se ficar `authorization`
        // (do default mesclado) + `Authorization` (recém-injetado), o servidor
        // pode pegar a versão velha → 401 no retry. Apagar ambas antes de setar.
        delete config.headers.authorization;
        delete config.headers.Authorization;
        config.headers.Authorization = `Bearer ${newToken}`;
        delete axiosInstance.defaults.headers.authorization;
        delete axiosInstance.defaults.headers.Authorization;
        axiosInstance.defaults.headers.Authorization = `Bearer ${newToken}`;
        return axiosInstance(config);
      } catch (refreshErr) {
        if (axiosInstance?.defaults?.headers) {
          delete axiosInstance.defaults.headers.authorization;
          delete axiosInstance.defaults.headers.Authorization;
        }
        redirectToLogin();
        return Promise.reject(error);
      }
    }
  );
}
